import { combine, createDomain, createEvent, createStore, sample } from 'effector';
import {
  ChartData,
  DeviceConnectedPayload,
  DeviceDisconnectedPayload,
  SensorDataPayload,
  TableDataRecord,
} from './types';
import { Device, DEVICE_LIST } from '../../../shared/configs/device-types';
import { getExperimentsByDeviceName } from '../../../shared/configs/experiments';

const resetDomain = createEvent();
const clearData = createEvent();
const domain = createDomain();

const onDeviceConnected = createEvent<DeviceConnectedPayload>();
const onSensorDataUpdated = createEvent<SensorDataPayload>();
const onDeviceDisconnected = createEvent<DeviceDisconnectedPayload>();
const onBluetoothDisconnected = createEvent<void>();

const $connectedDevice = domain.createStore<DeviceConnectedPayload | null>(null);

const $recordingStartedAt = domain.createStore<Date | null>(null);
const $recordingStarted = domain.createStore(false);

$connectedDevice
  .on(onDeviceConnected, (_, payload) => payload)
  .on(onSensorDataUpdated, (_, data) => ({
    deviceId: data.deviceId,
    deviceName: data.deviceName,
  }));

const setDeviceInfo = createEvent<Device>();
const $deviceInfo = domain.createStore<Device | null>(null);
const $experiments = $deviceInfo.map((device) => {
  return device?.name ? getExperimentsByDeviceName(device.name) : []
});

$deviceInfo.on(setDeviceInfo, (_, device) => device);

const $tableDataRaw = domain.createStore<SensorDataPayload[]>([]);


const $frequency = createStore(10);
const setFrequency = createEvent<number>();

$frequency
  .on(setFrequency, (_, frequency) => frequency)

export const sensorFrequencyModel = {
  $frequency,
  setFrequency
}

const $tableData = combine(
  $deviceInfo,
  $recordingStartedAt,
  $tableDataRaw,
  (deviceInfo, recordingStartedAt, tableDataRaw): TableDataRecord[] => {
    if (!recordingStartedAt || !deviceInfo) return [];

    const startedAtSec = Math.floor(recordingStartedAt.getTime() / 1000);

    const grouped: Record<number, number[]> = {};

    for (const record of tableDataRaw) {
      const recordTimeSec = Math.floor(new Date(record.timestamp).getTime() / 1000);

      const deltaSec = recordTimeSec - startedAtSec;

      if (deltaSec < 0) continue;

      if (!grouped[deltaSec]) {
        grouped[deltaSec] = [];
      }
      grouped[deltaSec].push(record.value);
    }

    const result: TableDataRecord[] = Object.entries(grouped).map(([deltaStr, values]) => {
      const delta = Number(deltaStr);
      const average = values.reduce((sum, v) => sum + v, 0) / values.length;

      return {
        time: delta,
        value: average,
        unit: deviceInfo.unit,
      };
    });

    result.sort((a, b) => b.time - a.time);

    return result;
  },
);

const $chartData = $tableData.map((records): ChartData => {
  const labels: string[] = [];
  const values: number[] = [];

  const data = [...records].reverse();

  for (const { time, value } of data) {
    labels.push(time.toString());
    values.push(value);
  }

  return {
    labels,
    values,
  };
});

const $stats = $tableData.map((records) => {
  if (records.length === 0) {
    return {
      min: null,
      max: null,
      avg: null,
    };
  }

  const min = Math.min(...records.map((record) => record.value));
  const max = Math.max(...records.map((record) => record.value));
  const avg = records.reduce((acc, record) => acc + record.value, 0) / records.length;

  return { min, max, avg };
});

const $lastValue = domain.createStore<number>(0);

sample({
  clock: onSensorDataUpdated,
  fn: (data) => data.value,
  target: $lastValue,
});

sample({
  clock: $recordingStarted,
  filter: Boolean,
  fn: () => new Date(),
  target: $recordingStartedAt,
});

sample({
  source: {
    records: $tableDataRaw,
    startedAt: $recordingStartedAt,
    deviceInfo: $deviceInfo,
  },
  clock: onSensorDataUpdated,
  filter: $recordingStarted,
  fn: ({ records, startedAt, deviceInfo }, data) => {
    if (!startedAt || !deviceInfo) return records;

    return [
      ...records,
      {
        ...data,
        timestamp: new Date().toISOString(),
      },
    ];
  },
  target: $tableDataRaw,
});

sample({
  clock: clearData,
  fn: () => [],
  target: $tableDataRaw,
});

sample({
  clock: onDeviceDisconnected,
  target: resetDomain,
});

sample({
  clock: onBluetoothDisconnected,
  target: resetDomain,
});

domain.onCreateStore((store) => store.reset(resetDomain));

const setExperimentId = createEvent<number>();

const $experimentId = domain
  .createStore<number | null>(null)
  .on(setExperimentId, (_, id) => id);

const $experiment = combine($experiments, $experimentId, (experiments, experimentId) =>
  experiments.find((experiment) => experiment.id === experimentId) ?? null,
);

sample({
  source: $experiment,
  clock: $experiments,
  filter: (experiment, experiments) => experiment === null && experiments.length > 0,
  fn: (_, experiments) => experiments[0]?.id ?? null,
  target: setExperimentId,
});

sample({
  source: $deviceInfo,
  clock: onSensorDataUpdated,
  fn: (_, data) => {
    const device = DEVICE_LIST.find((device) => device.key === data.friendlyName) ?? null;

    return device;
  },
  filter: (deviceInfo) => deviceInfo === null,
  target: $deviceInfo,
});

export const devicesEntity = {
  // external events to plug different APIs
  onDeviceConnected,
  onSensorDataUpdated,
  onDeviceDisconnected,
  onBluetoothDisconnected,

  // core stores
  $connectedDevice,
  $deviceInfo,
  $experiments,
  $tableDataRaw,
  $tableData,
  $chartData,
  $stats,
  $lastValue,
  $recordingStarted,
  $recordingStartedAt,

  // experiment selection
  $experimentId,
  $experiment,
  setExperimentId,

  $frequency,
  setFrequency,
  // control events
  setDeviceInfo,
  clearData,
  resetDomain,
};


